//================================================================
//  Component: Add Business Unit
//================================================================

//  Purpose: Add a new business unit to the assets collection

//  Properties:
//    - paneOpen = {useState, determines if the pane is open}
//    - setPaneOpen = {useState, allows you to open/close pane}

//  Example:
//    <AddAgreement
//      paneOpen={paneOpen}
//      setPaneOpen={setPaneOpen}
//    ></AddAgreement>    

//================================================================


//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components

//Functions
import WriteDocument from '../../../Library/WriteDocument';
import QueryDocument from '../../../Library/QueryDocument';

//Images
import LoadingIcon from '../../../Components/Images/Image_Loading_Ripple.svg';
import Error from '../../../Components/Images/Icon_ErrorX_Red.svg';


export default function AddBusinessUnit({
    paneOpen,
    setPaneOpen
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to determine the pane status > 'onload', 'pending', 'error'
    const [paneStatus, setPaneStatus] = useState('onload');

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    // https://react-hook-form.com/get-started
    const {
        register,
        handleSubmit,
        reset,
        watch,
        setError,
        clearErrors,
        formState: { isSubmitSuccessful, errors },
    } = useForm();

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Submit Handler
    const onSubmit = (formData) => {

        setPaneStatus('pending');

        //  Prepare Document
        const documentObject = {
            'id': `bu-${Date.now()}`,
            'name': formData.name,
            'address': {
                'streetaddressline1': formData.streetaddressline1,
                'streetaddressline2': formData.streetaddressline2,
                'city': formData.city,
                'state': formData.state,
                'postcode': formData.postcode,
            },

            'createdby': getUser.emailaddress,
            'created': new Date(),
            'modifiedby': '',
            'modified': new Date(),
            'status': 'active', // active or decommissioned
        }

        WriteDocument('businessunits', documentObject.id, documentObject, false).then(() => {

            // Reset form & close side pane
            reset();
            setPaneStatus('onload');
            setPaneOpen(false);

        }).catch((errors) => {

            console.log('error', errors);
            setPaneStatus('error');

        });

    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    useEffect(() => {

        if (isSubmitSuccessful) {

            reset();

        };

    }, [isSubmitSuccessful, reset]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    if (paneOpen === false) return null;

    //------------------------------------------------------
    //  Pending
    //------------------------------------------------------

    if (paneStatus === 'pending') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container flex justify-center items-center mb-[2%] p-[4%] rounded-[5px] text-center'>
                    <img alt='loading-circle-icon' src={LoadingIcon}></img>
                </dialog>
            </div>
        )
    }

    //------------------------------------------------------
    //  Onload
    //------------------------------------------------------

    else if (paneStatus === 'onload') {
        return (
            <div className='Pane-Background'>

                <dialog className='Pane-Container pb-0'>

                    <form className='flex flex-col gap-2 justify-between' onSubmit={handleSubmit(onSubmit)}>

                        {/* Header */}
                        <div>
                            <h4> Add Business Unit </h4>
                            <hr className='border-[1px] mb-0'></hr>

                            {/* ===================================================== */}
                            {/*  1. Name                                              */}
                            {/* ===================================================== */}

                            <div className='FormComponent-Row'>

                                <label className='font-medium'>1. Name <span className='text-[#C4314B]'>* </span></label>
                                <input
                                    className={errors.name ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    {
                                    ...register('name', {
                                        required: 'This field is required',
                                        onBlur: () => {

                                            QueryDocument('businessunits', [['name', '==', watch('name')]]).then((existingRequests) => {

                                                // Clear any errors
                                                if (existingRequests.length === 0) return clearErrors(['name']);

                                                // Found existing business unit
                                                setError(
                                                    'name', {
                                                    type: 'custom', message: 'Found an existing business unit with this name.'
                                                }
                                                )

                                            }).catch((error) => {

                                                return `Failed to validate business unit details. ${error}`

                                            });

                                        }
                                    })}
                                    placeholder='Construction'
                                    type='text'
                                ></input>
                                {errors.name && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.name?.message}</p>}

                            </div>

                            {/* ===================================================== */}
                            {/*  2. Contact Information                               */}
                            {/* ===================================================== */}

                            <div className='FormComponent-Row'>

                                <label className='font-medium'>2. Contact Information <span className='text-[#C4314B]'>* </span></label>

                                {/* Address Line 1 */}
                                <label className='font-medium'>Address Line 1 <span className='text-[#C4314B]'>* </span></label>
                                <input
                                    className={errors.streetaddressline1 ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    style={{ width: '610px' }}
                                    {
                                    ...register('streetaddressline1', {
                                        required: 'This field is required',
                                    })}
                                    placeholder='Floor 1'
                                    type='text'
                                ></input>
                                {errors.streetaddressline1 && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.streetaddressline1?.message}</p>}

                                {/* Address Line 2 */}
                                <label className='font-medium'>Address Line 2</label>
                                <input
                                    className={errors.streetaddressline2 ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    style={{ width: '610px' }}
                                    {
                                    ...register('streetaddressline2', {
                                        required: '',
                                    })}
                                    placeholder='1 John Street'
                                    type='text'
                                ></input>
                                {errors.streetaddressline2 && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.streetaddressline2?.message}</p>}


                                <div className='flex flex-row gap-2'>

                                    {/* City */}
                                    <div className='flex flex-col'>
                                        <label className=' font-medium'>City <span className='text-[#C4314B]'><span className='text-[#C4314B]'>* </span></span></label>
                                        <input
                                            className={errors.city ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                            style={{ width: '390px' }}
                                            {...register('city', {
                                                required: 'This field is required.',
                                            })}
                                            placeholder='Sydney'
                                        ></input>

                                        {errors.city && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.city?.message}</p>}
                                    </div>

                                    {/* State */}
                                    <div className='flex flex-col'>
                                        <label className='font-semibold'>State <span className='text-[#C4314B]'>* </span></label>
                                        <select
                                            className={errors.state ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                                            style={{ minWidth: '100px' }}
                                            {...register('state', {
                                                required: 'This field is required.',
                                            })}
                                        >
                                            <option hidden value=''>-</option>
                                            <option value='NSW'> NSW </option>
                                            <option value='VIC'> VIC </option>
                                            <option value='QLD'> QLD </option>
                                            <option value='SA'> SA </option>
                                            <option value='WA'> WA </option>
                                            <option value='NT'> NT </option>
                                            <option value='ACT'> ACT </option>
                                            <option value='TAS'> TAS </option>
                                        </select>
                                        {errors.state && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.state?.message}</p>}
                                    </div>

                                    {/* Post Code */}
                                    <div className='flex flex-col'>
                                        <label className='font-semibold'>Post Code <span className='text-[#C4314B]'>* </span></label>
                                        <input
                                            className={errors.postcode ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                            {...register('postcode', {
                                                required: 'This field is required.',
                                            })}
                                            placeholder='2000'
                                            style={{ width: '100px' }}
                                            type='number'
                                        ></input>
                                        {errors.postcode && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.postcode?.message}</p>}
                                    </div>

                                </div>

                            </div>

                        </div>

                        {/* ===================================================== */}
                        {/*  Button Container                                     */}
                        {/* ===================================================== */}

                        <div className='flex flex-row gap-2 sticky  right-[10px] pl-2 py-[10px]  border-t-[1px]'>

                            {/* Submit */}
                            <button
                                className='Primary-Button whitespace-nowrap'
                                disabled={false}
                                type='submit'
                                value='submit'
                            >
                                Submit
                            </button>

                            {/* Cancel */}
                            <button className='Secondary-Button whitespace-nowrap' onClick={() => {

                                // Close side pane
                                setPaneOpen(false);
                                reset();

                            }}>
                                Cancel
                            </button>
                        </div>

                    </form>

                </dialog >
            </div >
        )
    }

    //------------------------------------------------------
    //  Error
    //------------------------------------------------------

    else if (paneStatus === 'error') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container flex flex-col gap-2 justify-center items-center mb-[2%] p-[4%] rounded-[5px] text-center'>

                    <img src={Error} alt='error'></img>

                    <h4> Oops Something Went Wrong! </h4>

                    <p>
                        An error occurred while we processed your request.
                        <br></br>
                        If you feel this is a mistake, contact your site administrator.
                    </p>

                    <button className='Primary-Button' onClick={() => {
                        window.location.reload();
                    }}>
                        Try Again
                    </button>

                </dialog>
            </div>
        )
    }
}